import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import {
  MatLegacyTable as MatTable,
  MatLegacyTableDataSource as MatTableDataSource,
} from '@angular/material/legacy-table';
import { MatSort } from '@angular/material/sort';

import { IHallPassType } from 'libs/domain';
import { hall_pass_ng_grpc_pb, hall_pass_pb } from 'libs/generated-grpc-web';
import { HallPassTypeMapper } from 'libs/shared-grpc';
import { mingaSettingTypes } from 'libs/util';
import { BehaviorSubject, Observable, ReplaySubject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { HallPassService } from '@app/src/app/services/HallPass';
import { CacheService } from '@app/src/app/shared/services/cache/cache.service';
import { CacheKey } from '@app/src/app/shared/services/cache/cache.types';
import { MingaSettingsService } from '@app/src/app/store/Minga/services';

import {
  BackDropColor,
  ModalOverlayService,
  ModalOverlayServiceCloseEventType,
} from '@shared/components/modal-overlay';
import { SystemAlertSnackBarService } from '@shared/components/system-alert-snackbar';
import { KioskPermissionsService } from '@shared/services/kiosk/kiosk-permissions.service';

import { HpmTypesEditComponent } from '../hpm-types-edit';
import { newTypeRow } from './hpm-types-table.animations';
import { HP_TYPES_TABLE_COLUMNS, HpmTypesTableMessages } from './hpm-types-table.contants';

/**
 * Hall Pass Manager Types Table
 *
 * Uses v9 angular material table and sort
 *
 * @link https://v9.material.angular.io/components/table/overview
 * @link https://v9.material.angular.io/components/sort/overview
 */
@Component({
  selector: 'mg-hpm-types-table',
  templateUrl: './hpm-types-table.component.html',
  styleUrls: ['./hpm-types-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [newTypeRow],
})
export class HpmTypesTableComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Mat Table Component */
  @ViewChild(MatTable)
  table: MatTable<any>;

  /** Mat Sort Component */
  @ViewChild(MatSort, { static: false })
  sort: MatSort;

  /** Messages */
  public readonly MESSAGES = HpmTypesTableMessages;

  /** I don't know */
  readonly bannerImageSizes: ReadonlyArray<string | string[]> = [
    'blurloading1',
    ['longcardbanner', 'cardbanner', 'banner'],
  ];

  /** Loading State */
  private readonly _isLoadingSubject = new BehaviorSubject(false);
  public readonly isLoading$ = this._isLoadingSubject.asObservable();

  /** Subscription Cleanup Helper */
  private _destroyedSubject = new ReplaySubject<void>(1);

  /** Displayed Columns */
  public DISPLAYED_COLUMNS = HP_TYPES_TABLE_COLUMNS;

  /** Media Subscription */
  private readonly _mediaSub$ = this.mediaObserver.asObservable();

  /** Data Source */
  dataSource = new MatTableDataSource<IHallPassType>([]);

  /** New Type ID */
  newTypeId: number;

  /** Animation State */
  animationState = 'initial';

  public readonly manuallyEndedPassesSetting$ = this._mingaSettings
    .getSettingValueObs(mingaSettingTypes.PASS_MUST_MANUALLY_END)
    .pipe(takeUntil(this._destroyedSubject));

  @Input()
  onNewTypeCreated: Observable<IHallPassType>;
  _onNewTypeCreated$: Subscription;

  @Output()
  refetchTypes: EventEmitter<string> = new EventEmitter();

  /** Component Constructor */
  constructor(
    public mediaObserver: MediaObserver,
    public kioskPermissions: KioskPermissionsService,
    private _cdr: ChangeDetectorRef,
    private _modalOverlay: ModalOverlayService,
    private _systemAlertSnackBar: SystemAlertSnackBarService,
    private _hallPassManager: hall_pass_ng_grpc_pb.HallPassManager,
    private _hpm: HallPassService,
    private _mingaSettings: MingaSettingsService,
    private _cacheService: CacheService,
  ) {
    this.fetchTypes();
    this._mediaSubscription();
  }

  /** Component Lifecycle: On Mount */
  ngOnInit(): void {
    /** On New Type Created */
    this._onNewTypeCreated$ = this.onNewTypeCreated
      .pipe(takeUntil(this._destroyedSubject))
      .subscribe(d => this._handleOnNewTypeCreated(d));
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this._cdr.markForCheck();
  }

  /** Component Lifecycle: On Unmount */
  ngOnDestroy(): void {
    this._destroyedSubject.next();
    this._destroyedSubject.complete();
  }

  /**
   * Handle On Click Edit Type
   */
  public async onClickEditType(item: IHallPassType, isNewItem?: boolean): Promise<void> {
    if (!item) return;
    const { id } = item;
    const overlayData = {
      type: isNewItem ? 'new' : 'existing',
      data: isNewItem ? item : id,
    };
    const overlayRef = this._modalOverlay.open(HpmTypesEditComponent, {
      backdrop: BackDropColor.LIME,
      data: overlayData,
      disposeOnNavigation: false,
    });

    // After Overlay Has Closed
    overlayRef.afterClosed.subscribe(async d => {
      if (!d) return;
      const { type, data } = d;
      if (type === ModalOverlayServiceCloseEventType.SUBMIT) {
        if (data?.isNewType) {
          this._systemAlertSnackBar.success(HpmTypesTableMessages.SNACK_CREATE_SUCCESS);
          const hallpassType = data?.type;
          this.newTypeId = hallpassType.id;
          this.animationState = 'glow';
          this.dataSource.data = [hallpassType, ...this.dataSource.data];
          this._cdr.markForCheck();
          setTimeout(() => {
            this.newTypeId = undefined;
            this.animationState = 'initial';
            this._cdr.markForCheck();
          }, 1000);
        }
        this._systemAlertSnackBar.success(HpmTypesTableMessages.SNACK_UPDATE_SUCCESS);
        await this.fetchTypes();
        this._cacheService.clearCacheByNamespace(CacheKey.SELECTION_ASSIGNER_CAROUSEL);
      }
      if (type === ModalOverlayServiceCloseEventType.DELETE) {
        this._systemAlertSnackBar.success(HpmTypesTableMessages.SNACK_DELETE_SUCCESS);
        await this.fetchTypes();
        this._cacheService.clearCacheByNamespace(CacheKey.SELECTION_ASSIGNER_CAROUSEL);
      }
    });
  }

  public async fetchTypes(): Promise<void> {
    this._isLoadingSubject.next(true);
    try {
      const request = new hall_pass_pb.GetMingaHallPassTypesRequest();
      request.setGetActiveOnly(false);
      const response = await this._hallPassManager.getMingaHallPassTypes(request);
      const types = response.getHallPassTypeList().map(HPProto => HallPassTypeMapper.fromProto(HPProto));
      this.dataSource.data = types;
    } catch (error) {
      this._systemAlertSnackBar.error('Failed to fetch hall pass types');
    } finally {
      this._isLoadingSubject.next(false);
    }
  }

  public async onStatusChange(item: IHallPassType, value: boolean) {
    try {
      const hallPassType: IHallPassType = item;
      hallPassType.active = value;

      // restrictions are unaffected so there is no need to pass them along and upsert them
      hallPassType.restriction = undefined;

      await this._hpm.updateHallPassType(item);
      this._systemAlertSnackBar.success(HpmTypesTableMessages.SNACK_UPDATE_SUCCESS);
      this._cdr.markForCheck();
      this._cacheService.clearCacheByNamespace(CacheKey.SELECTION_ASSIGNER_CAROUSEL);
    } catch (error) {
      this._systemAlertSnackBar.error(HpmTypesTableMessages.SNACK_UPDATE_FAIL);
      this._cdr.markForCheck();
    }
  }

  private async _handleOnNewTypeCreated(type: IHallPassType): Promise<void> {
    this.onClickEditType(type, true);
  }

  public trackById(index: number, item: IHallPassType) {
    return item ? item.id : index;
  }

  private _mediaSubscription() {
    return this._mediaSub$.pipe(takeUntil(this._destroyedSubject)).subscribe(mediaChanges => {
      this._cdr.markForCheck();
    });
  }
}
